import { Alert, Card, Tabs, Typography } from 'antd';
import { useEffect, useState } from 'react';
import DealBookSwitch from '../components/common/DealBookSwitch';
import FixedDashboard from '../components/common/FixedDashboard';
import FixedInvestmentsTab from '../components/common/FixedInvestmentsTab';
import FixedProfitTab from '../components/common/FixedProfitTab';
import FixedLedgerTab from '../components/common/FixedLedgerTab';
import FixedWithdrawalsTab from '../components/common/FixedWithdrawalsTab';
import InvestmentsTab from '../components/investor/InvestmentsTab';
import LedgerTab from '../components/investor/LedgerTab';
import MyDashboard from '../components/investor/MyDashboard';
import ProfileTab from '../components/investor/ProfileTab';
import ProfitTab from '../components/investor/ProfitTab';
import WithdrawalsTab from '../components/investor/WithdrawalsTab';
import { useInvestor } from '../api/hooks';
import { useAuth } from '../context/AuthContext';
import { useTabHistory } from '../utils/useTabHistory';
import { useTranslation } from 'react-i18next';
import type { DealType } from '../types';

/**
 * Read-only portfolio view for investor-role logins. The write buttons inside the shared tabs are
 * hidden by `canWrite`, and the API rejects investor writes regardless of what the UI shows.
 *
 * PDFs live in one place here: the Ledger tab's statement. The per-transaction receipt downloads
 * that staff see are hidden from investors, so there is a single, unambiguous document to take away
 * rather than the same figures scattered across four tabs.
 */
export default function InvestorPortalPage() {
  const { t } = useTranslation();
  const tabs = useTabHistory('overview');
  const { user } = useAuth();
  const investorId = user?.investorId ?? 0;
  const { data: investor } = useInvestor(investorId);
  // Investors may hold both books; one selection is shared by every tab that has two sides.
  const [book, setBook] = useState<DealType>('PercentageBased');
  const openingBook = investor?.dealType;
  useEffect(() => {
    if (openingBook) setBook(openingBook);
  }, [openingBook]);

  if (!investorId) {
    return <Alert type="error" showIcon message="This account is not linked to an investor record." />;
  }

  return (
    <>
      <Typography.Title level={3}>My Portfolio</Typography.Title>

      <Card>
        <Tabs
          activeKey={tabs.activeKey}
          onChange={tabs.onChange}
          items={[
            {
              key: 'overview',
              label: t('tabs.overview'),
              children: (
                <DealBookSwitch
                  book={book}
                  onChange={setBook}
                  percentage={<MyDashboard investorId={investorId} />}
                  fixed={<FixedDashboard partyType="Investor" partyId={investorId} />}
                />
              ),
            },
            {
              key: 'ledger',
              label: t('tabs.ledger'),
              children: (
                <DealBookSwitch
                  book={book}
                  onChange={setBook}
                  percentage={<LedgerTab investorId={investorId} />}
                  fixed={<FixedLedgerTab partyType="Investor" partyId={investorId} />}
                />
              ),
            },
            {
              key: 'investments',
              label: 'My investments',
              children: (
                <DealBookSwitch
                  book={book}
                  onChange={setBook}
                  percentage={<InvestmentsTab investorId={investorId} />}
                  fixed={<FixedInvestmentsTab partyType="Investor" partyId={investorId} canWrite={false} />}
                />
              ),
            },
            {
              key: 'profit',
              label: t('tabs.profit'),
              children: (
                <DealBookSwitch
                  book={book}
                  onChange={setBook}
                  percentage={<ProfitTab investorId={investorId} />}
                  fixed={<FixedProfitTab partyType="Investor" partyId={investorId} />}
                />
              ),
            },
            {
              key: 'withdrawals',
              label: t('tabs.withdrawals'),
              children: (
                <DealBookSwitch
                  book={book}
                  onChange={setBook}
                  percentage={<WithdrawalsTab investorId={investorId} />}
                  fixed={<FixedWithdrawalsTab partyType="Investor" partyId={investorId} canWrite={false} />}
                />
              ),
            },
            { key: 'profile', label: t('tabs.profile'), children: <ProfileTab investorId={investorId} /> },
          ]}
        />
      </Card>
    </>
  );
}
